/**
 * Legacy Import Settings Page - Import historischer Rechnungsdaten aus CSV
 * Entspricht dem Design-Layout der anderen Settings-Seiten
 */

import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";

// Icons
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  FileUpload as FileUploadIcon,
  TextSnippet as TextSnippetIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  Analytics as AnalyticsIcon,
} from "@mui/icons-material";

// API Client und Types
import { apiClient } from "../../services/apiClient";
import {
  LegacyImportResult,
  LegacyImportValidation,
  LegacyInvoiceRow,
} from "../../types/api-types";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`legacy-import-tabpanel-${index}`}
      aria-labelledby={`legacy-import-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const LegacyImportSettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // State Management
  const [tabValue, setTabValue] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvText, setCsvText] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [validation, setValidation] = useState<LegacyImportValidation | null>(
    null
  );
  const [importResult, setImportResult] = useState<LegacyImportResult | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    resetState();
  };

  const resetState = () => {
    setSelectedFile(null);
    setCsvText("");
    setValidation(null);
    setImportResult(null);
    setError(null);
    setIsValidating(false);
    setIsImporting(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setValidation(null);
      setError(null);
    }
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleValidateFile = async () => {
    if (!selectedFile) return;

    setIsValidating(true);
    setError(null);

    try {
      const result = await apiClient.validateLegacyCsvFile(selectedFile);
      setValidation(result);
      if (result.isValid) {
        setShowPreviewDialog(true);
      }
    } catch (err: any) {
      setError(err.message || "Validierung fehlgeschlagen");
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidateText = async () => {
    if (!csvText.trim()) return;

    setIsValidating(true);
    setError(null);

    try {
      const result = await apiClient.validateLegacyCsvText(csvText);
      setValidation(result);
      if (result.isValid) {
        setShowPreviewDialog(true);
      }
    } catch (err: any) {
      setError(err.message || "Validierung fehlgeschlagen");
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    setIsImporting(true);
    setError(null);

    try {
      let result: LegacyImportResult;

      if (tabValue === 0 && selectedFile) {
        result = await apiClient.importLegacyInvoicesFromFile(selectedFile);
      } else if (tabValue === 1 && csvText.trim()) {
        result = await apiClient.importLegacyInvoicesFromText(csvText);
      } else {
        throw new Error("Keine gültigen Daten für Import verfügbar");
      }

      setImportResult(result);
      setShowPreviewDialog(false);
      setShowResultDialog(true);
    } catch (err: any) {
      setError(err.message || "Import fehlgeschlagen");
      setShowPreviewDialog(false);
    } finally {
      setIsImporting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton
                onClick={() => navigate("/settings")}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.2)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  📊 Legacy Rechnungsimport
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  Import historischer Rechnungsdaten aus CSV-Dateien
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Main Content */}
      <Paper
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: theme.shadows[2],
        }}
      >
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Legacy Import Tabs"
            variant={isMobile ? "fullWidth" : "standard"}
          >
            <Tab
              icon={<FileUploadIcon />}
              label="CSV-Datei Upload"
              id="legacy-import-tab-0"
              aria-controls="legacy-import-tabpanel-0"
            />
            <Tab
              icon={<TextSnippetIcon />}
              label="CSV-Text direkt"
              id="legacy-import-tab-1"
              aria-controls="legacy-import-tabpanel-1"
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <TabPanel value={tabValue} index={0}>
          {/* File Upload */}
          <Grid container spacing={3}>
            {/* Upload Area */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📁 CSV-Datei auswählen
                  </Typography>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />

                  <Box
                    sx={{
                      border: 2,
                      borderStyle: "dashed",
                      borderColor: selectedFile ? "success.main" : "grey.300",
                      borderRadius: 2,
                      p: 3,
                      textAlign: "center",
                      bgcolor: selectedFile
                        ? "success.50"
                        : "background.default",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "primary.50",
                      },
                    }}
                    onClick={handleFileUploadClick}
                  >
                    <CloudUploadIcon
                      sx={{
                        fontSize: 48,
                        color: selectedFile ? "success.main" : "grey.400",
                        mb: 2,
                      }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {selectedFile ? selectedFile.name : "CSV-Datei auswählen"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedFile
                        ? `${formatFileSize(
                            selectedFile.size
                          )} - Klicken zum Ändern`
                        : "Klicken oder Datei hierher ziehen"}
                    </Typography>
                  </Box>

                  {selectedFile && (
                    <Box sx={{ mt: 2 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                      >
                        <Button
                          variant="contained"
                          startIcon={<VisibilityIcon />}
                          onClick={handleValidateFile}
                          disabled={isValidating}
                          color="primary"
                        >
                          {isValidating ? (
                            <>
                              <CircularProgress size={16} sx={{ mr: 1 }} />
                              Validiere...
                            </>
                          ) : (
                            "Validieren & Vorschau"
                          )}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={() => setSelectedFile(null)}
                          color="error"
                        >
                          Entfernen
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* CSV Format Info */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📋 Erwartetes CSV-Format
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Die CSV-Datei muss folgende Spalten enthalten:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      bgcolor: "grey.100",
                      p: 2,
                      borderRadius: 1,
                      fontSize: "0.8rem",
                      overflow: "auto",
                      border: 1,
                      borderColor: "grey.300",
                    }}
                  >
                    {`"UUID","Belegnummer","Benutzer","ID","Name","Menge","Preis","MwSt","Datum","Vermerk"`}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    <strong>Wichtig:</strong> Rechnungen mit gleicher UUID
                    werden automatisch als Multi-Item-Rechnung gruppiert.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Text Input */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📝 CSV-Daten direkt eingeben
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={12}
                    value={csvText}
                    onChange={(e) => setCsvText(e.target.value)}
                    placeholder={`"UUID","Belegnummer","Benutzer","ID","Name","Menge","Preis","MwSt","Datum","Vermerk"
"6b671aaa-c9ac-4eea-8916-9f102f0574a4",1,Import,p_1,Hebebühne normal,1,11.5,,"2022-01-28 15:16",
...`}
                    variant="outlined"
                    sx={{ fontFamily: "monospace" }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<VisibilityIcon />}
                      onClick={handleValidateText}
                      disabled={!csvText.trim() || isValidating}
                      color="primary"
                    >
                      {isValidating ? (
                        <>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Validiere...
                        </>
                      ) : (
                        "Validieren & Vorschau"
                      )}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Error Display */}
        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              <AlertTitle>Fehler</AlertTitle>
              {error}
            </Alert>
          </Box>
        )}

        {/* Validation Results */}
        {validation && !validation.isValid && (
          <Box sx={{ p: 3 }}>
            <Alert severity="warning">
              <AlertTitle>Validierung fehlgeschlagen</AlertTitle>
              <List dense>
                {validation.errors.map((error: string, index: number) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <WarningIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText primary={error} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          </Box>
        )}
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          📊 Import Vorschau
          {validation && (
            <Typography variant="subtitle2" color="text.secondary">
              {validation.preview.totalRows} Zeilen,{" "}
              {validation.preview.uniqueInvoices} eindeutige Rechnungen
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {validation && (
            <Box>
              {/* Summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: "center" }}>
                      <DescriptionIcon
                        color="primary"
                        sx={{ fontSize: 40, mb: 1 }}
                      />
                      <Typography variant="h6">
                        {validation.preview.totalRows}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        CSV Zeilen
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: "center" }}>
                      <ReceiptIcon
                        color="success"
                        sx={{ fontSize: 40, mb: 1 }}
                      />
                      <Typography variant="h6">
                        {validation.preview.uniqueInvoices}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Rechnungen
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Sample Data */}
              <Typography variant="h6" gutterBottom>
                📋 Beispieldaten (erste 5 Zeilen)
              </Typography>
              <Box
                sx={{
                  maxHeight: 300,
                  overflow: "auto",
                  border: 1,
                  borderColor: "grey.300",
                  borderRadius: 1,
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #ddd",
                          fontSize: "0.8rem",
                        }}
                      >
                        UUID
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #ddd",
                          fontSize: "0.8rem",
                        }}
                      >
                        Beleg#
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #ddd",
                          fontSize: "0.8rem",
                        }}
                      >
                        Name
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #ddd",
                          fontSize: "0.8rem",
                        }}
                      >
                        Menge
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #ddd",
                          fontSize: "0.8rem",
                        }}
                      >
                        Preis
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          border: "1px solid #ddd",
                          fontSize: "0.8rem",
                        }}
                      >
                        Datum
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {validation.preview.sampleRows
                      .slice(0, 5)
                      .map((row: LegacyInvoiceRow, index: number) => (
                        <tr key={index}>
                          <td
                            style={{
                              padding: "8px",
                              border: "1px solid #ddd",
                              fontSize: "0.7rem",
                            }}
                          >
                            {row.UUID.substring(0, 8)}...
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              border: "1px solid #ddd",
                              fontSize: "0.8rem",
                            }}
                          >
                            {row.Belegnummer}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              border: "1px solid #ddd",
                              fontSize: "0.8rem",
                            }}
                          >
                            {row.Name}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              border: "1px solid #ddd",
                              fontSize: "0.8rem",
                            }}
                          >
                            {row.Menge}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              border: "1px solid #ddd",
                              fontSize: "0.8rem",
                            }}
                          >
                            {formatCurrency(parseFloat(row.Preis) || 0)}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              border: "1px solid #ddd",
                              fontSize: "0.8rem",
                            }}
                          >
                            {row.Datum}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreviewDialog(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={isImporting}
            startIcon={
              isImporting ? <CircularProgress size={16} /> : <UploadIcon />
            }
          >
            {isImporting ? "Importiere..." : "Import starten"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Result Dialog */}
      <Dialog
        open={showResultDialog}
        onClose={() => setShowResultDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {importResult?.success ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircleIcon color="success" />✅ Import erfolgreich
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ErrorIcon color="error" />❌ Import fehlgeschlagen
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {importResult && (
            <Box>
              {importResult.success ? (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent sx={{ textAlign: "center" }}>
                          <CheckCircleIcon
                            color="success"
                            sx={{ fontSize: 40, mb: 1 }}
                          />
                          <Typography variant="h6">
                            {importResult.importedCount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Importiert
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent sx={{ textAlign: "center" }}>
                          <WarningIcon
                            color="warning"
                            sx={{ fontSize: 40, mb: 1 }}
                          />
                          <Typography variant="h6">
                            {importResult.skippedCount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Übersprungen
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      📊 Import-Zusammenfassung
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Gesamte CSV-Zeilen"
                          secondary={importResult.summary.totalRows}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Eindeutige Rechnungen"
                          secondary={importResult.summary.uniqueInvoices}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Gesamtumsatz"
                          secondary={formatCurrency(
                            importResult.summary.totalAmount
                          )}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Zeitraum"
                          secondary={`${importResult.summary.dateRange.from} bis ${importResult.summary.dateRange.to}`}
                        />
                      </ListItem>
                    </List>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>Import-Fehler</AlertTitle>
                    Es sind Fehler beim Import aufgetreten.
                  </Alert>
                  {importResult.errors.length > 0 && (
                    <List dense>
                      {importResult.errors.map(
                        (error: string, index: number) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <ErrorIcon color="error" />
                            </ListItemIcon>
                            <ListItemText primary={error} />
                          </ListItem>
                        )
                      )}
                    </List>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowResultDialog(false);
              resetState();
            }}
            variant="contained"
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LegacyImportSettingsPage;
